// Iter 266 — the row that opens under a person on the Timeline tab: their whole
// day in one place — the full-width band chart, every break, and the apps and
// sites behind those bands. One fetch per person/day, cached by the caller.
import { Coffee, ExternalLink, Loader2 } from "lucide-react";
import { CLS_COLOR, CLS_LABEL, DayBandChart } from "@/components/prodTimelineShared";

const TAG_LABEL = { "on-call": "On call", PTO: "PTO", meeting: "Meeting", training: "Training", sick: "Sick", other: "Note" };

export function ProdTimelineDetail({ person, day, detailMin, showIdle }) {
  if (!day) {
    return (
      <div className="flex items-center gap-2 px-3 py-6 text-xs text-slate-500" data-testid={`prod-timeline-detail-loading-${person.key}`}>
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Opening {person.name}&apos;s day…
      </div>
    );
  }
  const apps = (day.apps || []).slice(0, 12);
  const breaks = person.breaks || [];
  return (
    <div className="space-y-4 border-t border-slate-100 bg-slate-50/60 px-3 py-4 dark:border-slate-800 dark:bg-slate-800/30"
      data-testid={`prod-timeline-detail-${person.key}`}>
      <div>
        <div className="mb-1.5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          Full day
          {day.tag && (
            <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold normal-case text-sky-700 dark:bg-sky-500/20 dark:text-sky-300">
              {TAG_LABEL[day.tag] || day.tag}
            </span>
          )}
        </div>
        <DayBandChart bands={day.bands} window={day.band_window} showIdle={showIdle}
          detailMin={detailMin} testid={`prod-timeline-detail-chart-${person.key}`} />
        {day.note && <p className="mt-2 text-xs italic text-slate-500 dark:text-slate-400">“{day.note}”</p>}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div>
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Breaks {breaks.length ? `· ${person.break_count} · ${person.break_hms} total` : ""}
          </div>
          {breaks.length === 0 ? (
            <p className="text-xs text-slate-400">No pause of 5 minutes or more inside the working session.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5" data-testid={`prod-timeline-breaks-${person.key}`}>
              {breaks.map((b) => (
                <span key={b.s} title={`${b.start} – ${b.end}`}
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${b.seconds >= 1800
                    ? "border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300"
                    : "border-slate-200 bg-white text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"}`}>
                  <Coffee className="h-3 w-3" /> {b.start} · {b.hms}
                </span>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="mb-1.5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Apps &amp; sites
            <a href={person.portal_url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-[10px] font-medium normal-case text-sky-600 hover:underline dark:text-sky-400"
              data-testid={`prod-timeline-portal-${person.key}`}>
              open in the tool <ExternalLink className="h-3 w-3" />
            </a>
          </div>
          {apps.length === 0 ? (
            <p className="text-xs text-slate-400">Nothing recorded for this day.</p>
          ) : (
            <ul data-testid={`prod-timeline-apps-${person.key}`}>
              {apps.map((a) => (
                <li key={`${a.name}-${a.is_app}`} className="flex items-center gap-2 border-b border-slate-100 py-1 text-xs last:border-0 dark:border-slate-800">
                  <span className="h-2 w-2 shrink-0 rounded-sm" title={CLS_LABEL[a.cls]}
                    style={{ backgroundColor: CLS_COLOR[a.cls] }} />
                  <span className="min-w-0 flex-1 truncate text-slate-700 dark:text-slate-200">{a.name}</span>
                  <span className="shrink-0 font-mono text-[11px] text-slate-500">{a.hms}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProdTimelineDetail;
